import { useEffect, useState, useCallback } from "react";
import { adminApi } from "@/lib/adminApi";
import { fmtDate } from "@/pages/admin/util";
import { Loader2, RefreshCw, Plus, HardHat, Check, X, Banknote, ChevronDown, ChevronUp } from "lucide-react";
import { toast } from "sonner";
import ContractorBadgeForName, { invalidateContractorHealth } from "@/components/admin/ContractorBadgeForName";
import ContractorHealthCard from "@/components/admin/ContractorHealthCard";

const STATUS_TABS = [
  { k: "ALL", label: "All" },
  { k: "AWAITING_APPROVAL", label: "Awaiting approval" },
  { k: "APPROVED", label: "Approved" },
  { k: "PAID", label: "Paid" },
  { k: "CANCELLED", label: "Cancelled" },
];

const STATUS_STYLES = {
  AWAITING_APPROVAL: "bg-[#B88A2A]/20 text-[#8B6814]",
  APPROVED: "bg-[#0F766E]/15 text-[#0F766E]",
  PAID: "bg-[#064E2E] text-white",
  CANCELLED: "bg-[#FCA5A5]/30 text-[#DC2626]",
};

export default function ContractorPayments() {
  const [tab, setTab] = useState("ALL");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [expandedHealth, setExpandedHealth] = useState({}); // {paymentId: contractor}

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const url = tab === "ALL" ? "/contractor-payments" : `/contractor-payments?status=${tab}`;
      const { data } = await adminApi.get(url);
      setItems(data.items || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not load contractor payments");
    } finally {
      setLoading(false);
    }
  }, [tab]);

  useEffect(() => { load(); }, [load]);

  async function setStatus(id, status, extras = {}) {
    setBusyId(id);
    try {
      await adminApi.post(`/contractor-payments/${id}/status`, { status, ...extras });
      toast.success(`Marked ${status.replace("_", " ").toLowerCase()}`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Update failed");
    } finally {
      setBusyId(null);
    }
  }

  return (
    <div className="space-y-6" data-testid="admin-contractor-payments-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <p className="eyebrow">Outbound payments</p>
          <h1 className="font-display text-[32px] lg:text-[40px] leading-[1.05] tracking-tight text-[#071A33] mt-2">Contractor Payments</h1>
          <p className="text-sm text-[#475569] mt-1 max-w-2xl">
            Ledger of payments owed to contractors. Money is moved via BACS in your bank — this records who, how much, what for, and tracks approval + paid state for audit.
          </p>
        </div>
        <div className="flex gap-2">
          <button onClick={load} className="btn-outline h-10 py-0 px-4 text-sm">
            <RefreshCw size={14} /> Refresh
          </button>
          <button onClick={() => setOpen(true)} className="btn-primary h-10 py-0 px-4 text-sm" data-testid="contractor-payment-new-btn">
            <Plus size={14} /> Record payment
          </button>
        </div>
      </div>

      <div className="flex gap-2 flex-wrap">
        {STATUS_TABS.map((t) => (
          <button
            key={t.k}
            onClick={() => setTab(t.k)}
            className={`px-4 py-2 text-sm rounded-full transition ${
              tab === t.k
                ? "bg-[#064E2E] text-white"
                : "bg-white border border-[#D7E7DF] text-[#475569] hover:border-[#064E2E]"
            }`}
            data-testid={`cp-tab-${t.k.toLowerCase()}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="space-y-3">
        {loading && (
          <div className="card-default text-center text-[#475569]">
            <Loader2 className="animate-spin inline" size={18} />
          </div>
        )}
        {!loading && items.length === 0 && (
          <div className="card-default text-center py-10" data-testid="cp-empty-state">
            <HardHat size={28} className="inline text-[#94A3B8] mb-3"/>
            <p className="text-[#071A33] font-medium">No contractor payments yet</p>
            <p className="text-xs text-[#475569] mt-1 max-w-md mx-auto">Log a payment owed to a contractor — track approval, BACS reference and payment date in one place.</p>
            <button onClick={() => setOpen(true)} className="btn-primary mt-4 text-sm" data-testid="cp-empty-cta">
              <Plus size={14}/> New payment
            </button>
          </div>
        )}
        {!loading && items.map((p) => (
          <div key={p.id} className="card-default" data-testid={`cp-card-${p.id}`}>
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div className="flex-1 min-w-[280px]">
                <div className="flex items-center gap-3 flex-wrap">
                  <h3 className="font-display text-lg text-[#071A33]">{p.contractor_name}</h3>
                  <ContractorBadgeForName
                    name={p.contractor_name}
                    onClick={(c) => setExpandedHealth((m) => ({ ...m, [p.id]: m[p.id] ? null : c }))}
                  />
                  <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${STATUS_STYLES[p.status]}`}>
                    {p.status.replace("_", " ")}
                  </span>
                </div>
                <p className="text-sm text-[#475569] mt-2">{p.description || "—"}</p>
                <div className="text-xs text-[#475569] mt-2 grid sm:grid-cols-2 gap-x-6 gap-y-1">
                  <div><span className="text-[#94A3B8]">Ref:</span> {p.reference}</div>
                  {p.contractor_email && <div><span className="text-[#94A3B8]">Email:</span> {p.contractor_email}</div>}
                  {p.contractor_phone && <div><span className="text-[#94A3B8]">Phone:</span> {p.contractor_phone}</div>}
                  {p.bank_sort_code && <div><span className="text-[#94A3B8]">Sort:</span> {p.bank_sort_code}</div>}
                  {p.bank_account_number && <div><span className="text-[#94A3B8]">Account:</span> {p.bank_account_number}</div>}
                  {p.support_case_id && <div><span className="text-[#94A3B8]">Case:</span> {p.support_case_id}</div>}
                  <div><span className="text-[#94A3B8]">Created:</span> {fmtDate(p.created_at, true)}</div>
                  {p.paid_at && <div><span className="text-[#94A3B8]">Paid:</span> {fmtDate(p.paid_at, true)} {p.payment_reference && `· ${p.payment_reference}`}</div>}
                </div>
                {expandedHealth[p.id] && (
                  <div className="mt-4">
                    <ContractorHealthCard
                      contractor={expandedHealth[p.id]}
                      onUpdated={(c) => {
                        setExpandedHealth((m) => ({ ...m, [p.id]: c }));
                        invalidateContractorHealth(c.name);
                      }}
                    />
                  </div>
                )}
              </div>
              <div className="flex flex-col items-end gap-3">
                <div className="font-display text-2xl text-[#071A33] tabular-nums">£{Number(p.amount).toFixed(2)}</div>
                <div className="flex flex-wrap gap-2 justify-end">
                  {p.status === "AWAITING_APPROVAL" && (
                    <>
                      <button onClick={() => setStatus(p.id, "APPROVED")} disabled={busyId === p.id} className="inline-flex items-center gap-1 rounded-full bg-[#0F766E] hover:bg-[#064E2E] text-white px-3 py-1.5 text-xs font-medium" data-testid={`cp-approve-${p.id}`}>
                        <Check size={12} /> Approve
                      </button>
                      <button onClick={() => setStatus(p.id, "CANCELLED")} disabled={busyId === p.id} className="inline-flex items-center gap-1 rounded-full bg-white border border-[#FCA5A5] text-[#DC2626] hover:bg-[#DC2626]/5 px-3 py-1.5 text-xs font-medium">
                        <X size={12} /> Cancel
                      </button>
                    </>
                  )}
                  {p.status === "APPROVED" && (
                    <>
                      <MarkPaidButton id={p.id} onPaid={(payload) => setStatus(p.id, "PAID", payload)} busy={busyId === p.id} />
                      <button onClick={() => setStatus(p.id, "CANCELLED")} disabled={busyId === p.id} className="inline-flex items-center gap-1 rounded-full bg-white border border-[#FCA5A5] text-[#DC2626] px-3 py-1.5 text-xs font-medium">
                        <X size={12} /> Cancel
                      </button>
                    </>
                  )}
                  {p.status === "PAID" && p.payment_method && (
                    <span className="text-xs text-[#475569]">via {p.payment_method}</span>
                  )}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {open && <NewContractorPaymentDialog onClose={() => setOpen(false)} onCreated={() => { setOpen(false); load(); }} />}
    </div>
  );
}

function MarkPaidButton({ id, onPaid, busy }) {
  const [showRef, setShowRef] = useState(false);
  const [ref, setRef] = useState("");
  if (!showRef) {
    return (
      <button onClick={() => setShowRef(true)} disabled={busy} className="inline-flex items-center gap-1 rounded-full bg-[#064E2E] hover:bg-[#0F766E] text-white px-3 py-1.5 text-xs font-medium" data-testid={`cp-mark-paid-${id}`}>
        <Banknote size={12} /> Mark paid
      </button>
    );
  }
  return (
    <div className="flex items-center gap-1">
      <input
        placeholder="BACS ref / txn id"
        value={ref}
        onChange={(e) => setRef(e.target.value)}
        className="rounded-full border border-[#D7E7DF] px-3 py-1.5 text-xs focus:outline-none focus:border-[#064E2E]"
        autoFocus
      />
      <button onClick={() => { onPaid({ payment_method: "BACS", payment_reference: ref }); setShowRef(false); }} className="inline-flex items-center gap-1 rounded-full bg-[#064E2E] text-white px-3 py-1.5 text-xs font-medium">
        <Check size={12} /> Confirm
      </button>
    </div>
  );
}

function NewContractorPaymentDialog({ onClose, onCreated }) {
  const [form, setForm] = useState({
    contractor_name: "",
    contractor_email: "",
    contractor_phone: "",
    bank_sort_code: "",
    bank_account_number: "",
    bank_account_name: "",
    amount: "",
    reference: "",
    description: "",
    support_case_id: "",
  });
  const [busy, setBusy] = useState(false);
  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  async function submit(e) {
    e.preventDefault();
    if (!form.contractor_name || !form.reference || !form.amount) {
      toast.error("Contractor name, reference and amount required");
      return;
    }
    setBusy(true);
    try {
      await adminApi.post("/contractor-payments", {
        ...form,
        amount: Number(form.amount),
      });
      toast.success("Contractor payment recorded");
      onCreated();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not create payment");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/60 p-4 overflow-y-auto" data-testid="new-contractor-payment-dialog">
      <form onSubmit={submit} className="max-w-2xl mx-auto bg-white rounded-2xl shadow-xl my-6">
        <div className="px-6 py-5 border-b border-[#E2DCD0] flex items-center gap-3">
          <HardHat size={20} className="text-[#064E2E]" />
          <h2 className="font-display text-2xl text-[#071A33]">Record contractor payment</h2>
        </div>
        <div className="p-6 space-y-4">
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="field-label">Contractor / supplier name</label>
              <input className="field-input" value={form.contractor_name} onChange={(e) => update("contractor_name", e.target.value)} required data-testid="cp-name" />
            </div>
            <div>
              <label className="field-label">Amount (£)</label>
              <input type="number" min="0" step="0.01" className="field-input" value={form.amount} onChange={(e) => update("amount", e.target.value)} required data-testid="cp-amount" />
            </div>
            <div>
              <label className="field-label">Reference</label>
              <input className="field-input" value={form.reference} onChange={(e) => update("reference", e.target.value)} placeholder="e.g. PHS-CASE-1234 roof" required data-testid="cp-reference" />
            </div>
            <div>
              <label className="field-label">Linked support case <span className="text-[#94A3B8] font-normal">(optional)</span></label>
              <input className="field-input" value={form.support_case_id} onChange={(e) => update("support_case_id", e.target.value)} placeholder="case_id" />
            </div>
            <div>
              <label className="field-label">Contractor email <span className="text-[#94A3B8] font-normal">(optional)</span></label>
              <input type="email" className="field-input" value={form.contractor_email} onChange={(e) => update("contractor_email", e.target.value)} />
            </div>
            <div>
              <label className="field-label">Contractor phone <span className="text-[#94A3B8] font-normal">(optional)</span></label>
              <input className="field-input" value={form.contractor_phone} onChange={(e) => update("contractor_phone", e.target.value)} />
            </div>
            <div className="sm:col-span-2 border-t border-[#E2DCD0] pt-4">
              <p className="eyebrow text-[#475569] text-[10px] mb-3">Bank details (for BACS transfer reference only)</p>
              <div className="grid sm:grid-cols-3 gap-3">
                <input className="field-input" placeholder="Account name" value={form.bank_account_name} onChange={(e) => update("bank_account_name", e.target.value)} />
                <input className="field-input" placeholder="Sort code" value={form.bank_sort_code} onChange={(e) => update("bank_sort_code", e.target.value)} />
                <input className="field-input" placeholder="Account number" value={form.bank_account_number} onChange={(e) => update("bank_account_number", e.target.value)} />
              </div>
            </div>
            <div className="sm:col-span-2">
              <label className="field-label">Description / scope</label>
              <textarea className="field-input min-h-[80px]" value={form.description} onChange={(e) => update("description", e.target.value)} placeholder="What the payment is for — keep it auditable" />
            </div>
          </div>
        </div>
        <div className="px-6 py-4 bg-[#F7F4ED] border-t border-[#E2DCD0] flex justify-end gap-2 rounded-b-2xl">
          <button type="button" onClick={onClose} className="btn-outline" disabled={busy}>Cancel</button>
          <button type="submit" disabled={busy} className="btn-primary" data-testid="cp-create-btn">
            {busy ? <><Loader2 className="animate-spin" size={16} /> Saving…</> : "Record payment"}
          </button>
        </div>
      </form>
    </div>
  );
}
